/**
 * Undo over confirm (DesignSystem 5.5 and 6.3): reversible actions run at once and offer
 * Undo in a toast for 6 seconds. Two shapes:
 *
 *   undoable({ title: 'Pipeline paused', undo: () => resume() });      // done already; Undo reverses it
 *   deferred({ title: 'Dismissed 3 items', commit: send, revert });    // applied locally; sent when the toast ends
 */
import { toast } from './toast.ts';

export const UNDO_MS = 6000;

export interface UndoableInput {
  title: string;
  description?: string;
  /** Reverse the action. Errors show as an error toast. */
  undo: () => void | Promise<void>;
  /** Shown after a successful undo. */
  undoneTitle?: string;
  duration?: number;
}

/** Toast with Undo for an action that already ran. Returns the toast id. */
export function undoable({ title, description, undo, undoneTitle, duration = UNDO_MS }: UndoableInput): string {
  return toast(title, {
    tone: 'success',
    description,
    duration,
    action: {
      label: 'Undo',
      onClick: () => {
        void (async () => {
          try {
            await undo();
            if (undoneTitle) toast(undoneTitle);
          } catch (e) {
            toast.error(e);
          }
        })();
      },
    },
  });
}

export interface DeferredInput {
  title: string;
  description?: string;
  /** Runs when the undo window closes without Undo. */
  commit: () => void | Promise<void>;
  /** Restores the optimistic local change when Undo is pressed (or the commit fails). */
  revert?: () => void;
  delay?: number;
}

export interface Deferred {
  /** Undo now: the commit never runs. */
  cancel: () => void;
  /** Commit now instead of waiting. */
  flush: () => Promise<void>;
}

/** Apply locally first, commit after the undo window closes. */
export function deferred({ title, description, commit, revert, delay = UNDO_MS }: DeferredInput): Deferred {
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = async () => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    try {
      await commit();
    } catch (e) {
      revert?.();
      toast.error(e);
    }
  };
  const cancel = () => {
    if (done) return;
    done = true;
    if (timer) clearTimeout(timer);
    revert?.();
  };
  timer = setTimeout(() => void flush(), delay);
  toast(title, { description, duration: delay, action: { label: 'Undo', onClick: cancel } });
  return { cancel, flush };
}
